import React, { useCallback, useEffect, useState } from 'react';

import { getMediaObjectFromUploads } from 'actions/addPost';
import KudosToggle from 'components/KudosToggle';
import PositiveNegativeSentimentToggle from 'components/PositiveNegativeSentimentToggle';
import TagRecommendations from 'components/TagRecommendations';
import VisibilityComponent from 'components/feed/PostVisibilityComponent';
import { useTagRecommendations, useVisibilityManuallyUpdated } from 'components/hooks/post';
import { useMediaUploadStateComment } from 'components/hooks/useMediaUploadState';
import { useDispatch } from 'react-redux';
import MediaPreview from 'screens/home/addPost/MediaPreview';
import UploadTypesView from 'screens/home/addPost/UploadTypesView';
import styled from 'styled-components';

import { COMMENT_EDIT_ERROR } from 'constants/ProjectConstants';

import { updateEditorStatePredictTagsAndVisbility } from 'services/helpers/post';
import { editComment } from 'services/post';
import { showErrorSnackbar } from 'services/snackbar';

import EditCommentComponent from './EditCommentComponent';
import useCommentReducer from './useCommentReducer';

// this is suppose to return a promise
const defaultEditComment = editComment;

export default function EditCommentContainer({
  id,
  contentState,
  media: initialMedia,
  feedId,
  feedType,
  toggleEditing,
  updateFeedComment,
  className,
  visibility: initialVisibility,
  sentiment: initialSentiment,
  placeholder,
  talkingPointQuestionId = null,
  allowVisibilityEdit = true,
  useTeamIcon = false,
  visibilityChangeDenyTooltip = '',
  isKudos = false,
  editComment = defaultEditComment,
  enableKudos = true,
}) {
  const {
    commentState,
    setEditorState,
    toggleIsUpdating,
    setUploadedFile,
    updateComment,
    clearComment,
    setGif,
    addTag,
    updateVisibility,
    updateSentiment,
  } = useCommentReducer(contentState, initialMedia, initialVisibility, initialSentiment);
  const { editorState, images = [], gifs = [], isUpdating, visibility, sentiment } = commentState;
  const uploadedFile = images[0];
  const uploadedGif = gifs[0];
  const { fileUrl, gifUrl, toggleGifModal, uploading, deleteFile, uploadFile } = useMediaUploadStateComment({
    setImage: setUploadedFile,
    uploadedImage: uploadedFile,
    uploadedGif,
    setGif,
  });
  const { recommendations, updateTags } = useTagRecommendations();
  const { visibilityManuallyUpdated, updateVisibilityManuallyStatus } = useVisibilityManuallyUpdated();

  const dispatch = useDispatch();

  const [commentAsKudos, setCommentAsKudos] = useState(isKudos);

  const onEscape = useCallback(() => {
    toggleEditing();
  }, [toggleEditing]);

  function saveComment() {
    toggleIsUpdating();
    const media = getMediaObjectFromUploads(images, gifs);
    editComment({
      commentId: id,
      feedId,
      commentEditorState: editorState,
      media,
      feedType,
      visibilityType: visibility,
      talkingPointQuestionId,
      sentiment,
      isKudos: commentAsKudos,
    })
      .then(({ comment }) => {
        // this comment only returns post and no other feed data
        // hence we don't need to normalize it before adding to
        // the feed redux store
        clearComment();
        // updateComment({ uploadedFile: null, isUpdating: false });
        updateFeedComment({ comment, commentId: id });
        toggleEditing();
      })
      .catch((err) => {
        toggleIsUpdating();
        dispatch(showErrorSnackbar(err.message || COMMENT_EDIT_ERROR));
      });
  }

  function onEditorStateChange(newEditorState) {
    updateEditorStatePredictTagsAndVisbility({
      editorState,
      newEditorState,
      setEditorState,
      visibilityManuallyUpdated,
      onAttributePredictionSuccess: (tags) => {
        updateTags(tags);
      },
      onVisibilityPredictionSuccess: (visibility) => {
        updateVisibility(visibility);
      },
    });
  }

  useEffect(() => {
    setCommentAsKudos(isKudos);
  }, [isKudos]);

  return (
    <Container className={className || ''}>
      <EditCommentComponent
        onEscape={onEscape}
        editorState={editorState}
        setEditorState={onEditorStateChange}
        uploadedFile={uploadedFile}
        onEnter={saveComment}
        handleBlur={saveComment}
        setUploadedFile={setUploadedFile}
        loading={isUpdating}
        placeholder={commentAsKudos ? 'Use @ to tag someone and # to include a competency' : 'Add a comment or praise'}
        enableKudos={enableKudos}
        MediaPreview={
          <MediaPreview
            fileUrl={fileUrl}
            deleteFile={deleteFile}
            uploading={uploading}
            uploadFile={uploadFile}
            setGif={setGif}
            gifUrl={gifUrl}
          />
        }
        UploadTypesView={
          <UploadTypesView
            uploadFile={uploadFile}
            uploadedImage={uploadedFile}
            toggleGifModal={toggleGifModal}
            gifUrl={gifUrl}
          />
        }
        VisibilityComponent={
          <VisibilityComponent
            visibility={allowVisibilityEdit ? visibility : initialVisibility}
            updateVisibility={allowVisibilityEdit ? updateVisibility : null}
            changeIsVisibilityUpdatedByUserStatus={updateVisibilityManuallyStatus}
            tooltip={allowVisibilityEdit ? null : visibilityChangeDenyTooltip}
            useTeamIcon={useTeamIcon}
          />
        }
        TagRecommendations={<TagRecommendations tagRecommendations={recommendations} addTag={addTag} />}
        SentimentComponent={<PositiveNegativeSentimentToggle selected={sentiment} updateSentiment={updateSentiment} />}
        KudosComponent={enableKudos && <KudosToggle selected={commentAsKudos} onChange={setCommentAsKudos} />}
      />
    </Container>
  );
}

const Container = styled.div``;
